import React, { useState, useEffect, useRef } from 'react';
import { backupAPI } from '../../services/api';
import toast from 'react-hot-toast';
import createAndDownloadBackup from '../../utils/backupDownload';

/**
 * Backup & Recovery — company data is exported as an encrypted, proprietary
 * .ledgerpro container. Files can only be opened by LedgerPro itself; they
 * contain ONLY this company's business data (never schema or system data).
 */
export default function BackupManager() {
  const [backups, setBackups] = useState([]); const [loading, setLoading] = useState(true); const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => { backupAPI.list().then(setBackups).catch(e=>toast.error(e.message)).finally(()=>setLoading(false)); };
  useEffect(load, []);

  const handleBackup = async() => { setCreating(true);
    try { await createAndDownloadBackup('Manual'); toast.success('Encrypted .ledgerpro backup downloaded'); load(); } catch(err){toast.error(err.message);} setCreating(false); };

  const openFilePicker = () => {
    if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); }
  };

  const handleFilePicked = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.ledgerpro')) {
      toast.error('Only .ledgerpro backup files can be restored');
      return;
    }
    setPendingFile(f);
  };

  const confirmRestore = async () => {
    if (!pendingFile) return;
    setRestoring(true);
    try {
      const result = await backupAPI.restore(pendingFile, pendingFile.name);
      toast.success(result.message || 'Backup restored successfully');
      setPendingFile(null);
      load();
    } catch (err) { toast.error(err.message); }
    setRestoring(false);
  };

  const downloadBackup = async (b) => {
    try {
      const blob = await backupAPI.download(b.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = b.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <input ref={fileInputRef} type="file" accept=".ledgerpro" style={{ display: 'none' }} onChange={handleFilePicked} />
      <div className="toolbar"><h2>Backup &amp; Recovery</h2><div className="toolbar-right">
        <button className="btn btn-outline" onClick={openFilePicker}>Restore from File</button>
        <button className="btn btn-primary" onClick={handleBackup} disabled={creating}>{creating?'Creating...':'Create Backup'}</button>
      </div></div>

      <div className="grid-2" style={{marginBottom:24}}>
        <div className="card"><div className="card-header"><h3>LedgerPro Secure Backups</h3></div>
          <div className="card-body">
            <p style={{marginBottom:12,color:'#64748b',fontSize:13}}>
              Your business data is exported into an encrypted, proprietary
              <strong> .ledgerpro</strong> file. It cannot be opened or edited with other
              tools — only LedgerPro can read it during a restore.
            </p>
            <ul style={{fontSize:13,color:'#475569',paddingLeft:20}}>
              <li>Only YOUR company's data is included — never other companies'</li>
              <li>Customers, vendors, invoices, bills & payments</li>
              <li>Chart of accounts and journal entries</li>
              <li>Bank accounts, deposits and transactions</li>
              <li>Products, inventory and fixed assets</li>
              <li>No database structure, system config or credentials</li>
            </ul>
          </div>
        </div>
        <div className="card"><div className="card-header"><h3>Restore</h3></div>
          <div className="card-body">
            <p style={{marginBottom:12,fontSize:13,color:'#64748b'}}>
              Upload a <strong>.ledgerpro</strong> file previously created by this company.
              LedgerPro verifies its integrity and ownership before restoring. The restore
              replaces the company's current business data with the backup's data and runs
              as a single all-or-nothing operation.
            </p>
            <button className="btn btn-outline" onClick={openFilePicker} disabled={restoring}>
              {restoring ? 'Restoring...' : 'Choose .ledgerpro File'}
            </button>
          </div>
        </div>
      </div>

      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="card"><div className="card-header"><h3>Backup History</h3></div>
          <div className="card-body" style={{padding:0}}>
            <table><thead><tr><th>Date</th><th>Type</th><th>Filename</th><th>Size</th><th>Status</th><th></th></tr></thead>
              <tbody>{backups.map(b=>(<tr key={b.id}>
                <td>{new Date(b.created_at).toLocaleString()}</td><td><span className="badge badge-draft">{b.backup_type}</span></td>
                <td className="font-mono">{b.filename}</td><td>{b.file_size ? (b.file_size/1024).toFixed(1)+' KB' : '-'}</td>
                <td><span className={`badge badge-${b.status==='Completed'?'posted':b.status==='Failed'?'voided':'draft'}`}>{b.status}</span></td>
                <td className="text-right">{b.status==='Completed' && b.filename.endsWith('.ledgerpro') &&
                  <button className="btn btn-sm btn-outline" onClick={()=>downloadBackup(b)}>Download</button>}</td>
              </tr>))}</tbody></table>
          </div>
        </div>
      )}

      {pendingFile && (
        <div className="modal-overlay" onClick={()=>setPendingFile(null)}><div className="modal" onClick={e=>e.stopPropagation()}>
          <div className="modal-header"><h3>Confirm Restore</h3><button className="btn-ghost" onClick={()=>setPendingFile(null)}>✕</button></div>
          <div className="modal-body">
            <p style={{marginBottom:8}}><strong>File:</strong> {pendingFile.name}</p>
            <p style={{marginBottom:8}}><strong>Size:</strong> {(pendingFile.size/1024).toFixed(1)} KB</p>
            <p style={{color:'#b91c1c',fontSize:13}}>
              Warning: restoring replaces your company's current business data
              (customers, invoices, transactions, etc.) with the contents of this backup.
              This cannot be undone.
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={()=>setPendingFile(null)}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={confirmRestore} disabled={restoring}>
              {restoring ? 'Restoring...' : 'Restore Now'}
            </button>
          </div>
        </div></div>
      )}
    </div>
  );
}
